import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getXeroBill,
  getXeroBillTickets,
  recordXeroBill,
} from "../services/xeroBillsService";
import expendituresService from "../services/expendituresService";
import styles from "./XeroBillRecord.module.css";

/**
 * Record a bill from Xero — P2P v1.
 *
 * One bill, already filled in from Xero, so the work is confirming the property and job
 * rather than typing the invoice.
 *
 * Deliberately separate from ExpendituresCreate, which is multi-row, starts from a property
 * rather than a bill, has no job field at all, and marks everything paid on creation.
 */

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

const fmtMoney = (v, currency = "GBP") =>
  v === null || v === undefined || v === ""
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: currency || "GBP" }).format(
        Number(v)
      );

const XeroBillRecord = () => {
  const { xeroInvoiceId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bill, setBill] = useState(null);
  const [suggestions, setSuggestions] = useState({ properties: [], tickets: [] });

  const [types, setTypes] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  // What the person is confirming. Everything factual comes from the bill on the server.
  const [form, setForm] = useState({
    property_id: "",
    type_id: "",
    ticket_id: "",
    bank_account_id: "",
    description: "",
    purchase_order_number: "",
  });

  const [propertySearch, setPropertySearch] = useState("");
  const [propertyResults, setPropertyResults] = useState([]);
  const [propertyName, setPropertyName] = useState("");
  const [tickets, setTickets] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [billRes, typesRes, banksRes] = await Promise.all([
        getXeroBill(xeroInvoiceId),
        expendituresService.getExpenditureTypes(),
        expendituresService.getBankAccounts(),
      ]);

      if (!billRes.success) {
        toast.error(billRes.message || "Could not load the bill");
        navigate("/xero-bills");
        return;
      }

      setBill(billRes.data.bill);
      setSuggestions(billRes.data.suggestions || { properties: [], tickets: [] });
      setTickets(billRes.data.suggestions?.tickets || []);

      const prefill = billRes.data.prefill || {};
      setForm((f) => ({
        ...f,
        property_id: prefill.property_id || "",
        // Left empty on purpose — see the note beside the field.
        bank_account_id: "",
        description: prefill.description || "",
      }));

      const top = billRes.data.suggestions?.properties?.[0];
      if (top) setPropertyName(top.property_name);

      setTypes(typesRes?.data || typesRes || []);
      setBankAccounts(banksRes?.data || banksRes || []);
    } catch (e) {
      toast.error(e.message || "Could not load the bill");
    } finally {
      setLoading(false);
    }
  }, [xeroInvoiceId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Changing the property changes which jobs are worth offering.
  const chooseProperty = async (id, name) => {
    setForm((f) => ({ ...f, property_id: id, ticket_id: "" }));
    setPropertyName(name);
    setPropertyResults([]);
    setPropertySearch("");

    try {
      const res = await getXeroBillTickets(xeroInvoiceId, id);
      setTickets(res.success ? res.data || [] : []);
    } catch {
      setTickets([]);
    }
  };

  const searchProperties = async (e) => {
    e.preventDefault();
    if (!propertySearch.trim()) return;
    try {
      const res = await expendituresService.searchProperties(propertySearch.trim());
      setPropertyResults(res?.data || res || []);
    } catch (err) {
      toast.error("Property search failed");
    }
  };

  const submit = async (e, { confirmDuplicate = false } = {}) => {
    if (e) e.preventDefault();

    if (!form.property_id) return toast.error("Choose a property");
    if (!form.type_id) return toast.error("Choose an expenditure type");

    setSaving(true);
    try {
      const res = await recordXeroBill(xeroInvoiceId, {
        ...form,
        ticket_id: form.ticket_id || null,
        bank_account_id: form.bank_account_id || null,
        confirm_duplicate: confirmDuplicate,
      });

      if (res.success) {
        toast.success("Expenditure recorded");
        navigate("/xero-bills");
        return;
      }

      /**
       * MyGlobe already holds this invoice from this supplier — usually because it was
       * typed in by hand, or because a match was rejected that should not have been.
       *
       * Asked rather than refused: an invoice number can legitimately repeat, and refusing
       * outright would strand a genuine bill. The existing record is named so the decision
       * is made on fact rather than on a warning.
       */
      if (res.status === 409 && res.code === "DUPLICATE_INVOICE") {
        const existing = res.data?.existing?.[0];
        const detail = existing
          ? `\n\nExisting record #${existing.id}: ${existing.creditor}, ` +
            `${Number(existing.amount).toFixed(2)}` +
            (existing.date ? ` on ${new Date(existing.date).toLocaleDateString("en-GB")}` : "")
          : "";
        if (window.confirm(`${res.message}${detail}\n\nRecord it anyway?`)) {
          setSaving(false);
          return submit(null, { confirmDuplicate: true });
        }
        return;
      }

      if (res.status === 409) {
        // Already recorded by someone else, or not yet approved in Xero.
        toast.warning(res.message);
        navigate("/xero-bills");
        return;
      }

      toast.error(res.message || "Could not record the expenditure");
    } catch (err) {
      toast.error(err.message || "Could not record the expenditure");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="container-fluid mt-4 text-muted">Loading…</div>;
  }
  if (!bill) return null;

  const isPaid = Number(bill.amount_paid) > 0;

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h4 className="mb-1">Record bill from Xero</h4>
          <p className="text-muted mb-0">
            The invoice details come from Xero. Confirm the property and job, then save.
          </p>
        </div>
        <button className="btn btn-outline-secondary" onClick={() => navigate("/xero-bills")}>
          Back to queue
        </button>
      </div>

      <div className="row">
        {/* ── From Xero — read only ───────────────────────────────────────── */}
        <div className="col-md-5">
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>From Xero</strong>
              {isPaid ? (
                <span className="badge bg-success">Paid {fmtDate(bill.fully_paid_on)}</span>
              ) : (
                <span className="badge bg-secondary">Outstanding</span>
              )}
            </div>
            <div className="card-body">
              <dl className="row mb-0">
                <dt className="col-5">Supplier</dt>
                <dd className="col-7">{bill.contact_name || "—"}</dd>

                <dt className="col-5">Invoice number</dt>
                <dd className="col-7">{bill.invoice_number || "—"}</dd>

                <dt className="col-5">Amount</dt>
                <dd className="col-7">{fmtMoney(bill.total, bill.currency)}</dd>

                {Number(bill.total_tax) > 0 && (
                  <>
                    <dt className="col-5 text-muted">of which VAT</dt>
                    <dd className="col-7 text-muted">{fmtMoney(bill.total_tax, bill.currency)}</dd>
                  </>
                )}

                <dt className="col-5">Invoice date</dt>
                <dd className="col-7">{fmtDate(bill.bill_date)}</dd>

                <dt className="col-5">Due</dt>
                <dd className="col-7">{fmtDate(bill.due_date)}</dd>

                <dt className="col-5">Amount paid</dt>
                <dd className="col-7">{fmtMoney(bill.amount_paid, bill.currency)}</dd>
              </dl>

              {/* Opens the bill in Xero, where the attached invoice is visible. The Dext
                  link held on the record needs a Dext login, which Finance generally
                  does not have. */}
              {bill.xero_url && (
                <a
                  className="btn btn-sm btn-outline-primary mt-3"
                  href={bill.xero_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View the invoice in Xero
                </a>
              )}
            </div>
          </div>
        </div>

        {/* ── What needs confirming ───────────────────────────────────────── */}
        <div className="col-md-7">
          <form onSubmit={submit}>
            <div className="card mb-3">
              <div className="card-header">
                <strong>Confirm</strong>
              </div>
              <div className="card-body">
                {/* Property */}
                <div className="mb-3">
                  <label className={`${styles.label} ${styles.required}`}>Property</label>

                  {form.property_id ? (
                    <div className="d-flex align-items-center">
                      <span className="me-2">{propertyName || `Property ${form.property_id}`}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-link"
                        onClick={() => {
                          setForm((f) => ({ ...f, property_id: "", ticket_id: "" }));
                          setPropertyName("");
                          setTickets([]);
                        }}
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="input-group">
                        <input
                          className="form-control"
                          placeholder="Search for a property"
                          value={propertySearch}
                          onChange={(e) => setPropertySearch(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && searchProperties(e)}
                        />
                        <button className="btn btn-outline-secondary" type="button" onClick={searchProperties}>
                          Search
                        </button>
                      </div>
                      {propertyResults.length > 0 && (
                        <ul className="list-group mt-2">
                          {propertyResults.slice(0, 8).map((p) => (
                            <li
                              key={p.id}
                              className="list-group-item list-group-item-action"
                              role="button"
                              onClick={() => chooseProperty(p.id, p.name || p.address)}
                            >
                              {p.name || p.address}
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}

                  {/* Suggestions from history. Shown as options rather than applied silently. */}
                  {!form.property_id && suggestions.properties?.length > 0 && (
                    <div className="mt-2">
                      <small className="text-muted d-block mb-1">
                        This supplier has billed these properties before:
                      </small>
                      {suggestions.properties.map((p) => (
                        <button
                          key={p.property_id}
                          type="button"
                          className="btn btn-sm btn-outline-secondary me-2 mb-2"
                          onClick={() => chooseProperty(p.property_id, p.property_name)}
                        >
                          {p.property_name}{" "}
                          <span className="text-muted">({p.times_billed}×)</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Job — optional, and the thing the existing form cannot do at all */}
                <div className="mb-3">
                  <label className={styles.label}>Job</label>
                  <select
                    className="form-select"
                    value={form.ticket_id}
                    onChange={(e) => setForm((f) => ({ ...f, ticket_id: e.target.value }))}
                    disabled={!form.property_id}
                  >
                    <option value="">No job — property cost</option>
                    {tickets.map((t) => (
                      <option key={t.ticket_id} value={t.ticket_id}>
                        {t.reference} — {t.name}
                        {t.supplier_matches ? " (this supplier)" : ""}
                      </option>
                    ))}
                  </select>
                  <small className={styles.hint}>
                    {form.property_id
                      ? tickets.length
                        ? "Open jobs at this property. Leave blank if this is not job-related."
                        : "No open jobs at this property."
                      : "Choose a property first."}
                  </small>
                </div>

                {/* Expenditure type */}
                <div className="mb-3">
                  <label className={`${styles.label} ${styles.required}`}>Expenditure type</label>
                  <select
                    className="form-select"
                    value={form.type_id}
                    onChange={(e) => setForm((f) => ({ ...f, type_id: e.target.value }))}
                    required
                  >
                    <option value="">Choose…</option>
                    {types.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {/* An empty list is a loading failure, not a valid state. Say so rather
                      than leaving a dropdown that appears broken. */}
                  {types.length === 0 && (
                    <small className="text-danger d-block mt-1">
                      No expenditure types loaded — the record cannot be saved. Check the
                      response from /api/expenditures/types.
                    </small>
                  )}
                </div>

                {/* Bank account */}
                <div className="mb-3">
                  <label className={styles.label}>Bank account</label>
                  <select
                    className="form-select"
                    value={form.bank_account_id}
                    onChange={(e) => setForm((f) => ({ ...f, bank_account_id: e.target.value }))}
                  >
                    <option value="">Not set</option>
                    {bankAccounts.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name || b.account_name}
                      </option>
                    ))}
                  </select>

                  {/* Not pre-filled on purpose. History is a prediction; Xero knows the
                      actual account once the bill is paid, and a value that looks chosen
                      would block that correction. The history is shown so it can be set
                      deliberately if wanted. */}
                  <small className={styles.hint}>
                    {suggestions.bank_accounts?.length > 0 ? (
                      <>
                        Optional — normally set from Xero once the bill is paid. Previously
                        used for this supplier:{" "}
                        {suggestions.bank_accounts
                          .map((b) => `${b.bank_account_name} (${b.times_used}×)`)
                          .join(", ")}
                      </>
                    ) : (
                      "Optional — normally set from Xero once the bill is paid."
                    )}
                  </small>
                </div>

                <div className="mb-3">
                  <label className={styles.label}>Description</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </div>

                <div className="mb-0">
                  <label className={styles.label}>Purchase order number</label>
                  <input
                    className="form-control"
                    value={form.purchase_order_number}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, purchase_order_number: e.target.value }))
                    }
                  />
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-outline-secondary me-2"
                onClick={() => navigate("/xero-bills")}
                disabled={saving}
              >
                Cancel
              </button>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? "Saving…" : "Record expenditure"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default XeroBillRecord;
